import InfoIcon from '@mui/icons-material/Info'
import Collapse from '@mui/material/Collapse'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import KNCaption from 'components/KNCaption/KNCaption'
import KNFormDate from 'components/KNForm/KNFormDate'
import KNFormRadioGroup from 'components/KNForm/KNFormRadioGroup'
import KNFormSignature from 'components/KNForm/KNFormSignature'
import KNFormSwitch from 'components/KNForm/KNFormSwitch'
import KNFormText from 'components/KNForm/KNFormText'
import KNFormTimeNext from 'components/KNForm/KNFormTimeNext'
import { TZDate } from '@date-fns/tz'
import { isValid } from 'date-fns'
import { resolveIsoTimeZone } from 'global/helpers/dates'
import { type ReactElement, useEffect, useState } from 'react'
import { useFormContext, type ValidateResult } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { AvailableStopStatus } from 'screens/TripDetails/TripDetails.types'

interface StatusUpdateFieldsProps {
  availableStatuses: AvailableStopStatus[]
  hasProof?: boolean
  showPaperless?: boolean
  shipmentNumbers?: ReactElement
  truckZoneMessage?: string
  timezoneReference?: string
  useDeviceTime?: boolean
  usePreciseTime?: boolean
  weblinkToken?: string
}

export const applyReferenceTimezoneOffset = (chosenDate: Date, chosenTime: Date, referenceDateTime: string): string =>
  new TZDate(
    chosenDate.getFullYear(),
    chosenDate.getMonth(),
    chosenDate.getDate(),
    chosenTime.getHours(),
    chosenTime.getMinutes(),
    chosenTime.getSeconds(),
    resolveIsoTimeZone(referenceDateTime) ?? 'Z',
  ).toISOString()

const StatusUpdateFields = ({
  availableStatuses,
  hasProof,
  showPaperless,
  shipmentNumbers,
  truckZoneMessage,
  timezoneReference = '',
  useDeviceTime, // hides date/time selectors, current time will be used on BE instead
  usePreciseTime,
  weblinkToken,
}: StatusUpdateFieldsProps): ReactElement => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const { watch, control, setValue, getValues, clearErrors } = useFormContext()
  const watchedStatusId = watch('statusId')
  const watchedCreatedAtDate = watch('createdAtDate')
  const watchedCreatedAtTime = watch('createdAtTime')
  const watchedPaperless = watch('paperless')
  const [selectedStatus, setSelectedStatus] = useState<AvailableStopStatus>()

  useEffect(() => {
    if (watchedStatusId) {
      const matchedStatus: AvailableStopStatus | undefined = availableStatuses.find(
        (status) => status.id === watchedStatusId,
      )
      if (matchedStatus) {
        setSelectedStatus(matchedStatus)
        setValue('statusCode', matchedStatus.code)
        setValue('reasonCode', matchedStatus.reasonCode)
      } else {
        setSelectedStatus(undefined)
        setValue('statusCode', '')
        setValue('reasonCode', '')
      }
    } else {
      setSelectedStatus(undefined)
      setValue('statusCode', '')
      setValue('reasonCode', '')
    }
  }, [watchedStatusId, availableStatuses])

  useEffect(() => {
    if (isValid(watchedCreatedAtDate) && isValid(watchedCreatedAtTime)) {
      // NOTE: rewrites input timezone to stop's timezone
      setValue('createdAt', applyReferenceTimezoneOffset(watchedCreatedAtDate, watchedCreatedAtTime, timezoneReference))
    } else {
      setValue('createdAt', undefined)
    }
  }, [watchedCreatedAtDate, watchedCreatedAtTime, timezoneReference])

  useEffect(() => {
    setValue('signature', null)
    clearErrors(['signee', 'signature'])
  }, [watchedPaperless])

  return (
    <Stack spacing={2}>
      <KNFormRadioGroup
        name="statusId"
        control={control}
        label={t('screens.cs.trip_details.update_status.status')}
        rules={{
          required: t('form.validation.status'),
        }}
        options={availableStatuses.map((status) => ({
          value: status.id,
          color: status.discrepancy ? 'error' : undefined,
          label: t(`waypoints.statuses.${status.id}`),
        }))}
        variant="outlined"
      />
      {shipmentNumbers}
      {!useDeviceTime && (
        <Stack spacing={1}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <KNFormDate
                name="createdAtDate"
                label={t('screens.cs.trip_details.update_status.execution_date')}
                control={control}
                rules={{
                  required: t('form.validation.required'),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <KNFormTimeNext
                name="createdAtTime"
                label={t('screens.cs.trip_details.update_status.execution_time')}
                control={control}
                rules={{
                  required: t('form.validation.required'),
                  validate: {
                    invalidDate: (value): ValidateResult =>
                      value == null || isValid(value) || t('form.validation.invalid_date'),
                  },
                }}
                precise={usePreciseTime}
              />
            </Grid>
          </Grid>
          {truckZoneMessage && (
            <KNCaption icon={<InfoIcon />}>{truckZoneMessage}</KNCaption>
          )}
        </Stack>
      )}
      {selectedStatus && selectedStatus.comments !== 'DISABLED' && (
        <KNFormText
          name="comment"
          label={
            selectedStatus.comments === 'REQUIRED'
              ? t('screens.cs.trip_details.update_status.remarks')
              : t('screens.cs.trip_details.update_status.remarks_optional')
          }
          control={control}
          rows={2}
          rules={{
            maxLength: {
              value: 70,
              message: t('screens.cs.trip_details.update_status.comment_length'),
            },
            validate: {
              commentsRequired: (value): ValidateResult => {
                if (!value && selectedStatus.comments === 'REQUIRED') {
                  return t('form.validation.required')
                }
                return true
              },
            },
          }}
        />
      )}
      {selectedStatus && selectedStatus.signee !== 'DISABLED' && (
        <KNFormText
          name="signee"
          label={
            selectedStatus.signee === 'REQUIRED' || getValues('paperless')
              ? t('screens.cs.trip_details.update_status.signee')
              : t('screens.cs.trip_details.update_status.signee_optional')
          }
          control={control}
          rules={{
            maxLength: {
              value: 70,
              message: t('screens.cs.trip_details.update_status.signee_length'),
            },
            validate: {
              signeeRequired: (value): ValidateResult => {
                if (!value && (selectedStatus.signee === 'REQUIRED' || getValues('paperless'))) {
                  return t('form.validation.required')
                }
                return true
              },
            },
          }}
        />
      )}
      {isMobile && weblinkToken && showPaperless && (
        <>
          <Stack>
            <KNFormSwitch
              name="paperless"
              label={t('screens.cs.trip_details.update_status.paperless')}
              control={control}
              disabled={hasProof}
            />
            {hasProof && (
              <KNCaption icon={<InfoIcon />}>
                {t('screens.cs.trip_details.upload_document.proof_already_attached')}
              </KNCaption>
            )}
          </Stack>
          <Collapse in={watchedPaperless} unmountOnExit>
            <KNFormSignature
              name="signature"
              control={control}
              rules={{
                required: t('form.validation.required'),
              }}
            />
          </Collapse>
        </>
      )}
    </Stack>
  )
}

export default StatusUpdateFields
